
import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

// UNIQUE VERSION IDENTIFIER
const PAGE_VERSION = 'v6.0-FINAL';
const BUILD_TIME = new Date().toISOString();

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!email || !password) {
      setError('Both email and password are required');
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (authError) {
        setError(authError.message);
        setIsLoading(false);
        return;
      }

      if (data.session) {
        window.REACT_APP_NAVIGATE('/admin/dashboard');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 via-emerald-500 to-teal-600 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        
        {/* VERSION BANNER - TOP */}
        <div className="bg-yellow-400 text-black font-black text-center py-4 px-6 rounded-t-2xl shadow-xl mb-0">
          <p className="text-2xl">🆕 {PAGE_VERSION} 🆕</p>
          <p className="text-xs mt-1">Built: {BUILD_TIME}</p>
        </div>
        
        {/* Main Card */}
        <div className="bg-white rounded-b-2xl shadow-2xl p-10">
          
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg">
              <i className="ri-admin-line text-white text-4xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
            <p className="text-gray-600">Enter BOTH email and password below</p>
          </div>

          {/* FIELD COUNTER */}
          <div className="bg-blue-100 border-2 border-blue-500 rounded-lg p-4 mb-6 text-center">
            <p className="text-blue-900 font-bold text-lg">
              ⚠️ YOU SHOULD SEE 2 INPUT FIELDS BELOW ⚠️
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            
            {/* EMAIL INPUT - FIELD #1 */}
            <div className="bg-yellow-50 border-4 border-yellow-400 rounded-lg p-4">
              <label htmlFor="email-field" className="block text-lg font-black text-gray-900 mb-3 uppercase">
                📧 FIELD #1: EMAIL ADDRESS
              </label>
              <input
                type="email"
                id="email-field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your-email@example.com"
                required
                autoComplete="email"
                className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-lg font-semibold"
              />
            </div>

            {/* PASSWORD INPUT - FIELD #2 */}
            <div className="bg-pink-50 border-4 border-pink-400 rounded-lg p-4">
              <label htmlFor="password-field" className="block text-lg font-black text-gray-900 mb-3 uppercase">
                🔑 FIELD #2: PASSWORD
              </label>
              <input
                type="password"
                id="password-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                autoComplete="current-password"
                className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-lg font-semibold"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-teal-600 text-white font-bold text-lg rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line text-xl animate-spin"></i>
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line text-xl"></i>
                  <span>Sign In to Dashboard</span>
                </>
              )}
            </button>
          </form>

          {/* Back Link */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <a
              href="/"
              className="text-gray-600 hover:text-green-600 transition-colors inline-flex items-center gap-2 cursor-pointer font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back to Home</span>
            </a>
          </div>
        </div>

        {/* Version Info - BOTTOM */}
        <div className="mt-6 bg-yellow-400 text-black font-black text-center py-4 px-6 rounded-2xl shadow-xl">
          <p className="text-xl">✅ {PAGE_VERSION} LOADED ✅</p>
          <p className="text-sm mt-1">{new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}
